/**
 * DGCarousel - tiny, dependency-free carousel used by Quickview and PDP.
 * - GPU-friendly: translate3d on a flex track
 * - Wrap-around navigation
 * - Pointer swipe (horizontal)
 * - Optional thumbnail syncing
 */
(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const states = new WeakMap();

  function getState(carousel) {
    let st = states.get(carousel);
    if (!st) {
      st = { isTransitioning: false, pendingDelta: 0, pointerId: null, startX: 0, startY: 0, transitionTimer: null };
      states.set(carousel, st);
    }
    return st;
  }

  function q(carousel, sel) {
    return carousel.querySelector(sel);
  }

  function qa(carousel, sel) {
    return Array.from(carousel.querySelectorAll(sel));
  }

  function getEls(carousel) {
    const track = q(carousel, '[data-dg-track]');
    const slides = qa(carousel, '[data-dg-slide]');
    const prevBtn = q(carousel, '[data-dg-prev]');
    const nextBtn = q(carousel, '[data-dg-next]');
    const counter = q(carousel, '[data-dg-counter]') || q(carousel, '[data-carousel-counter]');
    // Thumbs may live outside the carousel element (e.g. PDP thumbs strip)
    // If none found inside, look up to the nearest explicit scope wrapper.
    let thumbs = qa(carousel, '[data-dg-thumb]');
    if (!thumbs.length) {
      const scope = carousel.closest('[data-dg-carousel-scope]');
      if (scope) thumbs = qa(scope, '[data-dg-thumb]');
    }
    return { track, slides, prevBtn, nextBtn, counter, thumbs };
  }

  function setIndex(carousel, index) {
    carousel.dataset.index = String(index);
  }

  function getIndex(carousel) {
    return Number(carousel.dataset.index || '0') || 0;
  }

  function updateThumbs(thumbs, activeIndex) {
    if (!thumbs || !thumbs.length) return;
    thumbs.forEach((t, i) => {
      t.classList.toggle('is-selected', i === activeIndex);
      t.setAttribute('aria-current', i === activeIndex ? 'true' : 'false');
    });

    const active = thumbs[activeIndex];
    if (active && typeof active.scrollIntoView === 'function') {
      try {
        active.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', inline: 'center', block: 'nearest' });
      } catch (_) {
        // no-op
      }
    }
  }

  function updateCounter(counter, index) {
    if (!counter) return;
    const currentSpan = counter.querySelector('[data-current]');
    if (currentSpan) currentSpan.textContent = String(index + 1);
  }

  function updatePosition(carousel, index) {
    const { track, slides, counter, thumbs } = getEls(carousel);
    const count = slides.length;
    if (!track || count === 0) return;

    setIndex(carousel, index);
    track.style.transform = `translate3d(-${index * 100}%, 0, 0)`;
    updateCounter(counter, index);
    updateThumbs(thumbs, index);
  }

  function goTo(carousel, nextIndex, deltaForQueue = 0) {
    const { track, slides } = getEls(carousel);
    const count = slides.length;
    if (!track || count <= 1) return;

    const st = getState(carousel);

    if (prefersReducedMotion) {
      track.classList.remove('is-transitioning');
      st.isTransitioning = false;
      st.pendingDelta = 0;
      updatePosition(carousel, nextIndex);
      return;
    }

    if (st.isTransitioning) {
      if (deltaForQueue !== 0) st.pendingDelta = deltaForQueue;
      return;
    }

    st.isTransitioning = true;
    track.classList.add('is-transitioning');
    updatePosition(carousel, nextIndex);

    // Safety: if `transitionend` never fires (e.g. transition overridden to `none` by other CSS),
    // the carousel would get stuck in `isTransitioning` and "die". Force-reset after a ceiling.
    if (st.transitionTimer) {
      clearTimeout(st.transitionTimer);
      st.transitionTimer = null;
    }
    st.transitionTimer = setTimeout(() => {
      st.isTransitioning = false;
      track.classList.remove('is-transitioning');
      st.transitionTimer = null;
      if (st.pendingDelta) {
        const pending = st.pendingDelta;
        st.pendingDelta = 0;
        const current = getIndex(carousel);
        const next = (current + pending + count) % count;
        goTo(carousel, next, pending);
      }
    }, 650);

    const onEnd = (e) => {
      if (e.target !== track || e.propertyName !== 'transform') return;
      st.isTransitioning = false;
      track.classList.remove('is-transitioning');
      if (st.transitionTimer) {
        clearTimeout(st.transitionTimer);
        st.transitionTimer = null;
      }

      if (st.pendingDelta) {
        const pending = st.pendingDelta;
        st.pendingDelta = 0;
        const current = getIndex(carousel);
        const next = (current + pending + count) % count;
        goTo(carousel, next, pending);
      }
    };

    track.addEventListener('transitionend', onEnd, { once: true });
    track.addEventListener(
      'transitioncancel',
      () => {
        st.isTransitioning = false;
        track.classList.remove('is-transitioning');
        if (st.transitionTimer) {
          clearTimeout(st.transitionTimer);
          st.transitionTimer = null;
        }
      },
      { once: true }
    );
  }

  function next(carousel) {
    const { slides } = getEls(carousel);
    const count = slides.length;
    if (count <= 1) return;
    const current = getIndex(carousel);
    const delta = 1;
    const target = (current + delta) % count;
    goTo(carousel, target, delta);
  }

  function prev(carousel) {
    const { slides } = getEls(carousel);
    const count = slides.length;
    if (count <= 1) return;
    const current = getIndex(carousel);
    const delta = -1;
    const target = (current + delta + count) % count;
    goTo(carousel, target, delta);
  }

  function mount(carousel) {
    if (!carousel || carousel.__dgCarouselMounted) return;
    carousel.__dgCarouselMounted = true;

    const { track, slides, prevBtn, nextBtn, counter, thumbs } = getEls(carousel);
    const count = slides.length;
    if (!track || count === 0) return;

    // initialize
    setIndex(carousel, 0);
    track.style.transform = 'translate3d(0, 0, 0)';
    track.classList.remove('is-transitioning');
    const st = getState(carousel);
    st.isTransitioning = false;
    st.pendingDelta = 0;
    updateCounter(counter, 0);
    updateThumbs(thumbs, 0);

    if (count <= 1) {
      if (prevBtn) prevBtn.style.display = 'none';
      if (nextBtn) nextBtn.style.display = 'none';
      if (counter) counter.style.display = 'none';
    }

    // arrows
    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        next(carousel);
        if (e.detail !== 0) nextBtn.blur(); // prevent sticky hover/focus look on mouse click
      });
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        prev(carousel);
        if (e.detail !== 0) prevBtn.blur();
      });
    }

    // thumbs (PDP): use delegation on the thumbs strip for reliability
    const scope = carousel.closest('[data-dg-carousel-scope]');
    const thumbsContainer = scope ? scope.querySelector('[data-dg-thumbs]') : null;
    if (thumbsContainer && thumbs && thumbs.length && !thumbsContainer.__dgThumbsBound) {
      thumbsContainer.__dgThumbsBound = true;

      const TAP_THRESHOLD = 12; // px
      let downId = null;
      let downX = 0;
      let downY = 0;
      let downThumb = null;

      thumbsContainer.addEventListener(
        'pointerdown',
        (e) => {
          const t = e.target && e.target.closest && e.target.closest('[data-dg-thumb]');
          if (!t) return;
          downId = e.pointerId;
          downX = e.clientX;
          downY = e.clientY;
          downThumb = t;
        },
        { passive: true }
      );

      thumbsContainer.addEventListener(
        'pointerup',
        (e) => {
          if (downId == null || e.pointerId !== downId) return;
          const dx = e.clientX - downX;
          const dy = e.clientY - downY;
          const moved = Math.hypot(dx, dy);

          const t = (e.target && e.target.closest && e.target.closest('[data-dg-thumb]')) || downThumb;

          downId = null;
          downThumb = null;

          // If it was a scroll/drag, let it be (no navigation)
          if (moved > TAP_THRESHOLD) return;
          if (!t) return;

          e.preventDefault();
          const idx = Number(t.dataset.dgThumbIndex || t.getAttribute('data-dg-thumb-index') || '0') || 0;
          const safe = Math.max(0, Math.min(idx, count - 1));
          goTo(carousel, safe, 0);
        },
        { passive: false }
      );

      thumbsContainer.addEventListener(
        'pointercancel',
        () => {
          downId = null;
          downThumb = null;
        },
        { passive: true }
      );
    } else if (thumbs && thumbs.length) {
      // Fallback: if no container found, bind per-thumb
      thumbs.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const idx = Number(btn.dataset.dgThumbIndex || btn.getAttribute('data-dg-thumb-index') || '0') || 0;
          const safe = Math.max(0, Math.min(idx, count - 1));
          goTo(carousel, safe, 0);
        });
      });
    }

    // keyboard navigation when focused within carousel
    carousel.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      const active = document.activeElement;
      if (!active || !carousel.contains(active)) return;
      e.preventDefault();
      if (e.key === 'ArrowRight') next(carousel);
      else prev(carousel);
    });

    // swipe (pointer)
    carousel.addEventListener(
      'pointerdown',
      (e) => {
        if (e.target.closest('[data-dg-next], [data-dg-prev]')) return;
        if (count <= 1) return;
        st.pointerId = e.pointerId;
        st.startX = e.clientX;
        st.startY = e.clientY;
        try {
          carousel.setPointerCapture(st.pointerId);
        } catch (_) {
          // no-op
        }
      },
      { passive: true }
    );

    function endSwipe(e) {
      if (st.pointerId == null || e.pointerId !== st.pointerId) return;
      const dx = e.clientX - st.startX;
      const dy = e.clientY - st.startY;
      try {
        carousel.releasePointerCapture(st.pointerId);
      } catch (_) {
        // no-op
      }
      st.pointerId = null;

      if (Math.abs(dx) < 40) return;
      if (Math.abs(dx) <= Math.abs(dy)) return;

      // swipe left => next
      if (dx < 0) next(carousel);
      else prev(carousel);
    }

    carousel.addEventListener('pointerup', endSwipe, { passive: true });
    carousel.addEventListener(
      'pointercancel',
      () => {
        if (st.pointerId == null) return;
        try {
          carousel.releasePointerCapture(st.pointerId);
        } catch (_) {
          // no-op
        }
        st.pointerId = null;
      },
      { passive: true }
    );
  }

  function mountAll(root) {
    const scope = root || document;
    const carousels = scope.querySelectorAll('[data-dg-carousel]');
    carousels.forEach((c) => mount(c));
  }

  // Auto-mount on initial page load (PDP uses server-rendered markup)
  document.addEventListener('DOMContentLoaded', () => mountAll(document));

  window.DGCarousel = {
    mount,
    mountAll,
    goTo: (carousel, idx) => goTo(carousel, idx, 0),
    next,
    prev,
  };
})();


